import { ReactElement } from 'react';
import { Avatar, Box, Chip, Link, Paper, Typography } from '@mui/material';
import { IOrganisationActivationListResponse } from '../../types/@organisation';

type IProps = { activationList: IOrganisationActivationListResponse };

const OrganisationActivationListPanel = ({ activationList }: IProps): ReactElement => {
  const entries = activationList.entries || [];

  if (!entries.length) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#17324d' }}>Activation List</Typography>
        <Typography sx={{ mt: 1, color: '#6b7c8f' }}>CAP currently has no sponsors in the activation list for this organisation.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>Activation List</Typography>
        <Typography sx={{ mt: 0.5, color: '#6b7c8f' }}>
          Current CAP sponsor list · {entries.length} sponsor{entries.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      {entries.map((entry, index) => {
        const socials = entry.socialAccounts || {};
        return (
          <Paper key={`${entry.advertiserId || entry.businessName}-${index}`} variant="outlined" sx={{ p: 2, borderRadius: '14px' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar src={entry.logoUrl} alt={entry.businessName} variant="rounded" sx={{ width: 64, height: 64, bgcolor: '#f2f5f8' }}>
                {entry.businessName.slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#17324d' }}>{entry.businessName}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {entry.weighting !== undefined && <Chip size="small" label={`Weighting ${entry.weighting}`} />}
                  {entry.weeklyImpressions !== undefined && (
                    <Chip size="small" label={`Weekly Impressions ${entry.weeklyImpressions.toLocaleString()}`} />
                  )}
                  {entry.advertiserId && <Chip size="small" variant="outlined" label={`Advertiser ${entry.advertiserId}`} />}
                  {entry.campaignId && <Chip size="small" variant="outlined" label={`Campaign ${entry.campaignId}`} />}
                </Box>
                {(socials.x || socials.instagram || socials.tiktok) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                    {socials.x && <Typography variant="body2">X: {socials.x}</Typography>}
                    {socials.instagram && <Typography variant="body2">Instagram: {socials.instagram}</Typography>}
                    {socials.tiktok && <Typography variant="body2">TikTok: {socials.tiktok}</Typography>}
                  </Box>
                )}
                {entry.logoUrl && (
                  <Link href={entry.logoUrl} target="_blank" rel="noreferrer" sx={{ display: 'inline-block', mt: 1.5, fontSize: '14px' }}>
                    View sponsor logo
                  </Link>
                )}
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default OrganisationActivationListPanel;
